import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useShop } from "../context/ShopContext";

const NAV = [
  { label: "SHOP", href: "/shop" },
  { label: "THE SHELF", href: "/#shelf" },
  { label: "FIRST ACCESS", href: "/#drop-list" },
  { label: "CONTACT", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { cartCount, setCartOpen } = useShop();
  const reduce = useReducedMotion();
  const { pathname } = useLocation();
  const onDark = pathname === "/";
  const fg = onDark ? "text-ll-bone" : "text-ll-black";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-[60]">
        <div
          className={`transition-colors duration-300 ${scrolled ? (onDark ? "bg-ll-black/95 backdrop-blur-md border-b border-ll-line" : "bg-ll-bone/95 backdrop-blur-md border-b border-ll-black/10") : "bg-transparent"}`}
        >
          <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
            <Link to="/" data-testid="nav-logo" className={`flex items-center ${fg}`} aria-label="ETERNEL home">
              <span className="font-display text-2xl tracking-[0.12em] leading-none pt-0.5">ETERNEL</span>
            </Link>

            <div className="hidden items-center gap-8 md:flex">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`ll-navlink font-tech text-xs tracking-[0.2em] ${fg} transition-colors duration-200 hover:text-ll-red`}
                >
                  {n.label}
                </a>
              ))}
              <button
                data-testid="nav-cart-button"
                onClick={() => setCartOpen(true)}
                className={`relative flex items-center gap-2 font-tech text-xs tracking-[0.2em] ${fg} transition-colors duration-200 hover:text-ll-red`}
                aria-label={`Open cart, ${cartCount} items`}
              >
                CART
                <ShoppingBag className="h-4 w-4" />
                {cartCount > 0 && (
                  <span data-testid="cart-count-badge" className="absolute -right-4 -top-2 flex h-4 w-4 items-center justify-center bg-ll-red font-tech text-[10px] text-ll-bone">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            <div className="flex items-center gap-4 md:hidden">
              <button data-testid="mobile-cart-button" onClick={() => setCartOpen(true)} className={`relative ${fg}`} aria-label="Open cart">
                <ShoppingBag className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center bg-ll-red font-tech text-[10px] text-ll-bone">{cartCount}</span>
                )}
              </button>
              <button data-testid="mobile-menu-button" onClick={() => setMenuOpen(true)} className={fg} aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-ll-black"
            initial={reduce ? { opacity: 1 } : { clipPath: "inset(0 0 100% 0)" }}
            animate={reduce ? { opacity: 1 } : { clipPath: "inset(0 0 0% 0)" }}
            exit={reduce ? { opacity: 0 } : { clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-display text-2xl tracking-[0.12em]">ETERNEL</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <X className="h-7 w-7" />
              </button>
            </div>
            <div className="mt-8 h-px bg-ll-red" />
            <nav className="flex flex-col gap-2 px-5 pt-10">
              {[{ label: "ETERNEL", href: "/" }, ...NAV, { label: "ORDER STATUS", href: "/order-status" }].map((n, i) => (
                <motion.button
                  key={n.label}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  initial={reduce ? false : { x: -32, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: reduce ? 0 : 0.15 + i * 0.06, duration: 0.3 }}
                  onClick={() => { setMenuOpen(false); window.location.href = n.href; }}
                  className="text-left font-display text-5xl tracking-wide text-ll-bone transition-colors duration-200 hover:text-ll-red"
                >
                  {n.label}
                </motion.button>
              ))}
            </nav>
            <p className="absolute bottom-8 left-5 font-tech text-[11px] tracking-[0.2em] text-ll-steel">
              DECOR THAT CHANGES THE ROOM
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
